import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../Utils/axiosInstance";
import { toast } from "react-hot-toast";

const initialStateBlogReview = {
    reviews: [],
    userReview: null,
    averageRating: 0,
    totalReviews: 0,
    success: false,
    message: '',
    loading: false,
};

const handleErrors = (error, dispatch, rejectWithValue) => {
    const errorMessage = error.response?.data?.message || 'An error occurred';
    return rejectWithValue(error.response?.data || { message: errorMessage });
};

// Create a new blog review
export const createBlogReview = createAsyncThunk(
    "blogReview/createBlogReview",
    async (reviewData, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(`/createBlogReview`, reviewData);
            toast.success(response.data.message || "Review added successfully");
            return response.data.data;
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || "Failed to add review");
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

// Get all reviews for a specific blog
export const getBlogReviews = createAsyncThunk(
    "blogReview/getBlogReviews",
    async (blogId, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/getBlogReviews/${blogId}`);
            return response.data.data;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

// Get all blog reviews (admin)
export const getAllBlogReviews = createAsyncThunk(
    "blogReview/getAllBlogReviews",
    async (_, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/getAllBlogReviews`);
            return response.data.data;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

// Get user's review for a specific blog
export const getUserBlogReview = createAsyncThunk(
    "blogReview/getUserBlogReview",
    async (blogId, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/getUserBlogReview/${blogId}`);
            return response.data.data;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

// Update a blog review
export const updateBlogReview = createAsyncThunk(
    "blogReview/updateBlogReview",
    async ({ id, reviewData }, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.put(`/updateBlogReview/${id}`, reviewData);
            toast.success(response.data.message || "Review updated successfully");
            return response.data.data;
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || "Failed to update review");
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

// Delete a blog review
export const deleteBlogReview = createAsyncThunk(
    "blogReview/deleteBlogReview",
    async (id, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.delete(`/deleteBlogReview/${id}`);
            toast.success(response.data.message || "Review deleted successfully");
            return id;
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || "Failed to delete review");
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

const blogReviewSlice = createSlice({
    name: 'blogReview',
    initialState: initialStateBlogReview,
    reducers: {
        clearUserReview: (state) => {
            state.userReview = null;
        },
        clearReviews: (state) => {
            state.reviews = [];
            state.averageRating = 0;
            state.totalReviews = 0;
        }
    },
    extraReducers: (builder) => {
        builder
            // Create blog review
            .addCase(createBlogReview.pending, (state) => {
                state.loading = true;
                state.message = 'Adding review...';
            })
            .addCase(createBlogReview.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.message = 'Review added successfully';
                state.reviews.unshift(action.payload);
                state.userReview = action.payload;
                state.totalReviews = state.reviews.length;
                state.averageRating = state.reviews.length > 0
                    ? parseFloat((state.reviews.reduce((acc, curr) => acc + curr.rating, 0) / state.reviews.length).toFixed(1))
                    : 0;
            })
            .addCase(createBlogReview.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || 'Failed to add review';
            })

            // Get blog reviews
            .addCase(getBlogReviews.pending, (state) => {
                state.loading = true;
                state.message = 'Fetching reviews...';
            })
            .addCase(getBlogReviews.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.message = 'Reviews fetched successfully';
                state.reviews = action.payload.reviews || [];
                state.averageRating = parseFloat(action.payload.averageRating) || 0;
                state.totalReviews = action.payload.totalReviews || 0;
            })
            .addCase(getBlogReviews.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || 'Failed to fetch reviews';
            })

            // Get all blog reviews
            .addCase(getAllBlogReviews.pending, (state) => {
                state.loading = true;
                state.message = 'Fetching all reviews...';
            })
            .addCase(getAllBlogReviews.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.message = 'All reviews fetched successfully';
                state.reviews = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(getAllBlogReviews.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || 'Failed to fetch all reviews';
            })

            // Get user blog review
            .addCase(getUserBlogReview.pending, (state) => {
                state.loading = true;
                state.message = 'Fetching your review...';
            })
            .addCase(getUserBlogReview.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.message = 'User review fetched successfully';
                state.userReview = action.payload;
            })
            .addCase(getUserBlogReview.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || 'Failed to fetch user review';
            })

            // Update blog review
            .addCase(updateBlogReview.pending, (state) => {
                state.loading = true;
                state.message = 'Updating review...';
            })
            .addCase(updateBlogReview.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.message = 'Review updated successfully';
                state.reviews = state.reviews.map((review) =>
                    review._id === action.payload._id ? action.payload : review
                );
                if (state.userReview?._id === action.payload._id) {
                    state.userReview = action.payload;
                }
                state.averageRating = state.reviews.length > 0
                    ? parseFloat((state.reviews.reduce((acc, curr) => acc + curr.rating, 0) / state.reviews.length).toFixed(1))
                    : 0;
            })
            .addCase(updateBlogReview.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || 'Failed to update review';
            })

            // Delete blog review
            .addCase(deleteBlogReview.pending, (state) => {
                state.loading = true;
                state.message = 'Deleting review...';
            })
            .addCase(deleteBlogReview.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.message = 'Review deleted successfully';
                state.reviews = state.reviews.filter((review) => review._id !== action.payload);
                if (state.userReview?._id === action.payload) {
                    state.userReview = null;
                }
                state.totalReviews = state.reviews.length;
                state.averageRating = state.reviews.length > 0
                    ? parseFloat((state.reviews.reduce((acc, curr) => acc + curr.rating, 0) / state.reviews.length).toFixed(1))
                    : 0;
            })
            .addCase(deleteBlogReview.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || 'Failed to delete review';
            });
    }
});

export const { clearUserReview, clearReviews } = blogReviewSlice.actions;
export default blogReviewSlice.reducer;
